import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, resolveBackendAssetUrl } from "../../lib/api";
import { toast } from "sonner";
import { Bell, Camera, Image as ImageIcon, MonitorPlay, Plus, QrCode, Save, ScanQrCode, Trash2, Upload, Video } from "lucide-react";

const EMPTY = {
  type: "image",
  title: "",
  active: true,
  order: 10,
  duration: 10,
  file_url: "",
  fit: "contain",
  channel: "info",
};

const MODE_LABELS = {
  default: "Predeterminado",
  mirror_info: "Duplicar información",
  mirror_social: "Duplicar social",
};

const MODE_OPTIONS = [
  { value: "default", label: "Predeterminado" },
  { value: "mirror_info", label: "Duplicar información" },
  { value: "mirror_social", label: "Duplicar social" },
];

const CHANNEL_LABELS = {
  info: "Información",
  social: "Social",
};

const TYPE_LABELS = {
  image: "Imagen",
  video: "Vídeo",
  qr_carta: "QR Carta",
  gallery_photo: "Foto visitante",
  alert: "Aviso activo",
  gallery_cta: "CTA subir foto",
};

const TYPE_ICONS = {
  image: ImageIcon,
  video: Video,
  qr_carta: QrCode,
  gallery_photo: Camera,
  alert: Bell,
  gallery_cta: ScanQrCode,
};

const FILELESS_TYPES = new Set(["qr_carta", "gallery_photo", "alert", "gallery_cta"]);

export default function AdminPantallas() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const [draftFile, setDraftFile] = useState(null);
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ mode: "default", updated_at: "" });
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState("");
  const [settingsError, setSettingsError] = useState("");

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [items]);

  const infoItems = useMemo(() => sortedItems.filter((item) => (item.channel || "info") === "info"), [sortedItems]);
  const socialItems = useMemo(() => sortedItems.filter((item) => (item.channel || "info") === "social"), [sortedItems]);

  const lastUpdatedLabel = useMemo(() => {
    if (!settings.updated_at) return "Sin cambios guardados";
    const date = new Date(settings.updated_at);
    if (Number.isNaN(date.getTime())) return "Sin cambios guardados";
    return date.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  }, [settings.updated_at]);

  const load = async () => {
    setLoading(true);
    try {
      const [playlistResponse, settingsResponse] = await Promise.all([
        api.get("/screen-playlist"),
        api.get("/screen-settings"),
      ]);
      const data = playlistResponse.data;
      setItems(Array.isArray(data) ? data : []);
      if (settingsResponse.data) {
        setSettings({
          mode: settingsResponse.data.mode || "default",
          updated_at: settingsResponse.data.updated_at || "",
        });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const uploadFile = async (type, file) => {
    if (!file) return "";
    const maxSize = type === "video" ? 80 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(type === "video" ? "Máximo 80 MB" : "Máximo 10 MB");
      throw new Error("Archivo demasiado grande");
    }
    const fd = new FormData();
    fd.append("media_type", type);
    fd.append("file", file);
    const { data } = await api.post("/screen-playlist/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data.url;
  };

  const create = async () => {
    setBusy("create");
    try {
      const file_url = FILELESS_TYPES.has(draft.type) ? "" : await uploadFile(draft.type, draftFile);
      await api.post("/screen-playlist", { ...draft, file_url });
      setDraft(EMPTY);
      setDraftFile(null);
      toast.success("Elemento creado");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const changeMode = async (mode) => {
    setSettingsBusy(true);
    setSettingsMessage("Guardando...");
    setSettingsError("");
    try {
      const { data } = await api.put("/screen-settings", { mode });
      setSettings({
        mode: data.mode || "default",
        updated_at: data.updated_at || "",
      });
      setSettingsMessage("Modo actualizado correctamente");
    } catch (e) {
      setSettingsMessage("");
      setSettingsError(formatApiError(e.response?.data?.detail));
    } finally {
      setSettingsBusy(false);
    }
  };

  const updateLocal = (id, patch) => {
    setItems((current) => current.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  };

  const save = async (item) => {
    setBusy(item.id);
    try {
      let file_url = item.file_url || "";
      if (item._file && !FILELESS_TYPES.has(item.type)) {
        file_url = await uploadFile(item.type, item._file);
      }
      await api.patch(`/screen-playlist/${item.id}`, {
        type: item.type,
        title: item.title,
        active: item.active,
        order: Number(item.order) || 0,
        duration: Number(item.duration) || 10,
        file_url: FILELESS_TYPES.has(item.type) ? "" : file_url,
        fit: item.fit,
        channel: item.channel || "info",
      });
      toast.success("Guardado");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const remove = async (item) => {
    if (!confirm(`¿Eliminar "${item.title}"?`)) return;
    setBusy(item.id);
    try {
      await api.delete(`/screen-playlist/${item.id}`);
      toast.success("Elemento eliminado");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-pantallas">
      <header>
        <h1 className="font-display font-black text-3xl">Pantallas</h1>
        <p className="text-muted-foreground">Playlist automática para la ruta pública /pantalla.</p>
      </header>

      <section className="bg-white rounded-2xl border border-border p-5 space-y-4" data-testid="screen-mode">
        <div className="flex items-center gap-2">
          <MonitorPlay size={18} className="text-primary" />
          <h2 className="font-display font-bold">Modo de pantallas</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {MODE_OPTIONS.map((option) => {
            const active = settings.mode === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => changeMode(option.value)}
                disabled={settingsBusy || active}
                className={`rounded-full px-4 py-2 text-sm font-bold disabled:opacity-60 ${
                  active
                    ? "bg-primary text-primary-foreground"
                    : "bg-accent text-foreground hover:bg-accent/80"
                }`}
                data-testid={`screen-mode-${option.value}`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        <div className="grid gap-1 text-sm">
          <div>
            <span className="font-bold">Modo actual:</span> {MODE_LABELS[settings.mode] || MODE_LABELS.default}
          </div>
          <div>
            <span className="font-bold">Última actualización:</span> {lastUpdatedLabel}
          </div>
          {settingsMessage && <div className="font-bold text-primary">{settingsMessage}</div>}
          {settingsError && <div className="font-bold text-destructive">{settingsError}</div>}
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-border p-5 space-y-4" data-testid="screen-create">
        <div className="flex items-center gap-2">
          <MonitorPlay size={18} className="text-primary" />
          <h2 className="font-display font-bold">Nuevo elemento</h2>
        </div>
        <ScreenFields value={draft} onChange={setDraft} />
        {!FILELESS_TYPES.has(draft.type) && (
          <FileInput
            type={draft.type}
            file={draftFile}
            onFile={setDraftFile}
            testid="screen-new-file"
          />
        )}
        <button
          type="button"
          onClick={create}
          disabled={busy === "create"}
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-full font-bold disabled:opacity-60"
          data-testid="screen-create-btn"
        >
          <Plus size={16} />
          {busy === "create" ? "Creando..." : "Crear"}
        </button>
      </section>

      <section className="space-y-6 lg:grid lg:grid-cols-2 lg:items-start lg:gap-5 lg:space-y-0">
        {loading ? (
          <div className="bg-white rounded-2xl border border-border p-6 text-muted-foreground lg:col-span-2">Cargando playlist...</div>
        ) : sortedItems.length === 0 ? (
          <div className="bg-white rounded-2xl border border-border p-8 text-center text-muted-foreground lg:col-span-2">
            Todavía no hay elementos en la playlist.
          </div>
        ) : (
          <>
            <PlaylistSection
              title="Información"
              items={infoItems}
              busy={busy}
              empty="No hay elementos de información."
              onChange={updateLocal}
              onSave={save}
              onRemove={remove}
            />
            <PlaylistSection
              title="Social + publicidad"
              items={socialItems}
              busy={busy}
              empty="No hay elementos sociales."
              onChange={updateLocal}
              onSave={save}
              onRemove={remove}
            />
          </>
        )}
      </section>
    </div>
  );
}

function ScreenFields({ value, onChange, compact = false }) {
  const update = (patch) => onChange({ ...value, ...patch });
  const inputClass = compact
    ? "w-full rounded-lg border border-border px-3 py-2 lg:px-2 lg:py-1.5"
    : "w-full rounded-lg border border-border px-3 py-2";
  const gridClass = compact ? "grid md:grid-cols-2 gap-3 lg:gap-2" : "grid md:grid-cols-2 gap-3";
  return (
    <div className={gridClass}>
      <Field label="Tipo">
        <select
          value={value.type}
          onChange={(e) => update({ type: e.target.value, file_url: FILELESS_TYPES.has(e.target.value) ? "" : value.file_url })}
          className={inputClass}
        >
          <option value="image">Imagen</option>
          <option value="video">Vídeo</option>
          <option value="qr_carta">QR Carta</option>
          <option value="gallery_photo">Foto visitante</option>
          <option value="alert">Aviso activo</option>
          <option value="gallery_cta">CTA subir foto</option>
        </select>
      </Field>
      <Field label="Título interno">
        <input
          value={value.title}
          onChange={(e) => update({ title: e.target.value })}
          className={inputClass}
          placeholder={TYPE_LABELS[value.type]}
        />
      </Field>
      <Field label="Canal">
        <select
          value={value.channel || "info"}
          onChange={(e) => update({ channel: e.target.value })}
          className={inputClass}
        >
          <option value="info">Información</option>
          <option value="social">Social</option>
        </select>
      </Field>
      <Field label="Orden">
        <input
          type="number"
          value={value.order}
          onChange={(e) => update({ order: Number(e.target.value) })}
          className={inputClass}
        />
      </Field>
      <Field label="Duración (segundos)">
        <input
          type="number"
          min="1"
          value={value.duration}
          onChange={(e) => update({ duration: Number(e.target.value) })}
          className={inputClass}
        />
      </Field>
      <Field label="Ajuste">
        <select
          value={value.fit}
          onChange={(e) => update({ fit: e.target.value })}
          className={inputClass}
          disabled={value.type !== "image" && value.type !== "gallery_photo"}
        >
          <option value="contain">Contain</option>
          <option value="cover">Cover</option>
        </select>
      </Field>
      <label className={`inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 ${compact ? "lg:px-2 lg:py-1.5" : ""}`}>
        <input
          type="checkbox"
          checked={value.active}
          onChange={(e) => update({ active: e.target.checked })}
        />
        Activo
      </label>
    </div>
  );
}

function PlaylistSection({ title, items, busy, empty, onChange, onSave, onRemove }) {
  const activeCount = items.filter((item) => item.active).length;
  return (
    <div className="space-y-3 rounded-2xl border border-border bg-white p-4 lg:max-h-[68vh] lg:overflow-y-auto lg:p-3">
      <div className="sticky top-0 z-10 -mx-4 -mt-4 border-b border-border bg-white px-4 py-3 lg:-mx-3 lg:-mt-3 lg:px-3">
        <h2 className="font-display font-bold text-xl lg:text-lg">{title}</h2>
        <p className="text-sm font-semibold text-muted-foreground">
          {items.length} elementos · {activeCount} activos
        </p>
      </div>
      {items.length === 0 ? (
        <div className="rounded-xl border border-border bg-accent/30 p-6 text-muted-foreground">{empty}</div>
      ) : (
        items.map((item) => (
          <PlaylistCard
            key={item.id}
            item={item}
            busy={busy === item.id}
            onChange={(patch) => onChange(item.id, patch)}
            onSave={() => onSave(item)}
            onRemove={() => onRemove(item)}
          />
        ))
      )}
    </div>
  );
}

function PlaylistCard({ item, busy, onChange, onSave, onRemove }) {
  const Icon = TYPE_ICONS[item.type] || MonitorPlay;
  const previewUrl = resolveBackendAssetUrl(item.file_url);
  return (
    <article className="rounded-2xl border border-border bg-white p-4 space-y-4 lg:rounded-xl lg:p-3 lg:space-y-3" data-testid={`screen-item-${item.id}`}>
      <div className="flex items-start gap-4 lg:gap-3">
        <div className="h-24 w-36 shrink-0 overflow-hidden rounded-xl bg-accent border border-border grid place-items-center lg:h-16 lg:w-24 lg:rounded-lg">
          {item.type === "image" && previewUrl ? (
            <img src={previewUrl} alt={item.title} className="h-full w-full object-cover" />
          ) : item.type === "video" && previewUrl ? (
            <video src={previewUrl} className="h-full w-full object-cover" muted preload="metadata" />
          ) : (
            <Icon size={30} className="text-muted-foreground" />
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-3 lg:space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-accent px-2 py-1 text-xs font-bold">
              <Icon size={13} />
              {TYPE_LABELS[item.type] || item.type}
            </span>
            <span className={`rounded-full px-2 py-1 text-xs font-bold ${item.active ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
              {item.active ? "Activo" : "Inactivo"}
            </span>
            <span className="rounded-full bg-muted px-2 py-1 text-xs font-bold text-muted-foreground">
              {CHANNEL_LABELS[item.channel || "info"] || CHANNEL_LABELS.info}
            </span>
          </div>
          <ScreenFields value={item} onChange={onChange} compact />
          {!FILELESS_TYPES.has(item.type) && (
            <FileInput type={item.type} file={item._file} onFile={(file) => onChange({ _file: file })} />
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onSave}
              disabled={busy}
              className="inline-flex items-center gap-1 bg-primary text-primary-foreground px-3 py-2 rounded-full text-sm font-bold disabled:opacity-60"
            >
              <Save size={14} />
              {busy ? "Guardando..." : "Guardar"}
            </button>
            <button
              type="button"
              onClick={onRemove}
              disabled={busy}
              className="inline-flex items-center gap-1 bg-destructive text-destructive-foreground px-3 py-2 rounded-full text-sm font-bold disabled:opacity-60"
            >
              <Trash2 size={14} />
              Eliminar
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

function FileInput({ type, file, onFile, testid }) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-accent/40 px-3 py-2 text-sm font-medium" data-testid={testid}>
      <Upload size={15} />
      <span>{file ? file.name : type === "video" ? "Subir MP4 o MOV" : "Subir imagen"}</span>
      {type === "video" && <span className="text-xs text-muted-foreground">Formatos: MP4 o MOV · máximo 80 MB</span>}
      <input
        type="file"
        accept={type === "video" ? "video/mp4,video/quicktime,.mp4,.mov" : ".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"}
        className="hidden"
        onChange={(e) => {
          const selected = e.target.files?.[0];
          if (selected) onFile(selected);
          e.target.value = "";
        }}
      />
    </label>
  );
}

function Field({ label, children }) {
  return (
    <label className="block text-sm">
      <span className="block font-bold mb-1">{label}</span>
      {children}
    </label>
  );
}
